import { useEffect } from 'react'
import { useQueryClient, type QueryKey } from '@tanstack/react-query'
import { supabase } from './supabase'
import { createLogger } from './logger'

/**
 * Single Supabase Realtime channel that fans out `postgres_changes` into
 * TanStack Query `invalidateQueries` calls.
 *
 * Why this exists: each table-backed hook used to open its own channel
 * (`bookmarks-${Date.now()}`, `tags-${Date.now()}`, ...). At N hooks × M tabs
 * per user × K users you hit Supabase's connection limits fast, and the
 * "which table invalidates which cache key" mapping was scattered across
 * every hook. Consolidating here means:
 *
 *   - One WebSocket per tab instead of N.
 *   - A single place to see the table → query-key routing table.
 *   - Adding a new table-backed hook = one line in the registry below.
 *
 * Two filter shapes in play:
 *   - `user`-scoped tables  — filtered by `user_id=eq.${userId}`
 *   - `team`-scoped tables  — filtered by `team_id=eq.${sharedTeamId}`
 *                             (currently only `templates`; tags + folders
 *                             are user-scoped by product design). The
 *                             user's own personal-team template changes
 *                             arrive via a *separate* filter so the user's
 *                             own inserts still invalidate even without a
 *                             shared team.
 *   - Profiles is a special case — filtered by `id=eq.${userId}`.
 */

const log = createLogger('realtime')

type Scope = 'user' | 'team'

interface TableRoute {
  table: string
  scope: Scope
  key: (id: string) => QueryKey
}

/**
 * One entry per Postgres table whose changes should invalidate a TanStack
 * Query. TQ prefix-matching covers sub-keys (e.g. invalidating
 * `['categoryAssignments', userId]` hits
 * `['categoryAssignments', userId, 'urn:…']`).
 *
 * Keep in sync with the `*Key()` helpers exported from each hook.
 *
 * This array is the declared source of truth for Supabase Realtime
 * publication membership. CI (`scripts/check-realtime-parity.sh`,
 * wired into `.github/workflows/staging-migrations.yml`) fails any PR
 * that adds a table here without a corresponding
 * `alter publication supabase_realtime add table …` migration. See
 * ADR 0039.
 */
export const REALTIME_TABLES: ReadonlyArray<TableRoute> = [
  // User-scoped
  { table: 'bookmarks',            scope: 'user', key: (u) => ['bookmarks', u] },
  { table: 'drafts',               scope: 'user', key: (u) => ['drafts', u] },
  { table: 'tags',                 scope: 'user', key: (u) => ['tags', u] },
  { table: 'tag_assignments',      scope: 'user', key: (u) => ['tagAssignments', u] },
  { table: 'categories',           scope: 'user', key: (u) => ['categories', u] },
  { table: 'category_assignments', scope: 'user', key: (u) => ['categoryAssignments', u] },
  { table: 'followups',            scope: 'user', key: (u) => ['reminders', u] },
  { table: 'profiles',             scope: 'user', key: (u) => ['profile', u] },
  // Team-scoped (only templates — tags/categories are user-scoped).
  { table: 'templates',            scope: 'team', key: (u) => ['templates', u] },
]

interface Props {
  userId: string | null
  /**
   * The user's shared team id, if they have one. Null when the user is only
   * in their personal team — in that case team-scoped subscriptions fall
   * back to a user-scoped filter (their own inserts still invalidate).
   */
  sharedTeamId: string | null
}

export function RealtimeBridge({ userId, sharedTeamId }: Props) {
  const qc = useQueryClient()

  useEffect(() => {
    if (!userId) return

    // Coalesce invalidation calls so a single change that matches multiple
    // filters (e.g. user_id AND team_id for a template the user wrote into
    // their shared team) only triggers ONE invalidate per query key per
    // microtask tick. Without this, every user-write on a team-scoped
    // table fires two invalidations — doubled refetch cost, doubled
    // network chatter, and cache thrash when the two responses race.
    const pending = new Map<string, QueryKey>()
    let flushing = false
    const invalidate = (queryKey: QueryKey) => {
      pending.set(JSON.stringify(queryKey), queryKey)
      if (flushing) return
      flushing = true
      queueMicrotask(() => {
        for (const key of pending.values()) qc.invalidateQueries({ queryKey: key })
        pending.clear()
        flushing = false
      })
    }

    // Channel name includes sharedTeamId so gaining or losing a team
    // rebuilds the channel cleanly instead of trying to re-wire filters
    // in-flight.
    const channel = supabase.channel(`fwdslash:user:${userId}:team:${sharedTeamId ?? 'none'}`)

    for (const route of REALTIME_TABLES) {
      const queryKey = route.key(userId)

      if (route.table === 'profiles') {
        channel.on(
          'postgres_changes',
          { event: '*', schema: 'public', table: route.table, filter: `id=eq.${userId}` },
          () => invalidate(queryKey),
        )
        continue
      }

      if (route.scope === 'team' && sharedTeamId) {
        // Teammate-driven changes on the shared team.
        channel.on(
          'postgres_changes',
          { event: '*', schema: 'public', table: route.table, filter: `team_id=eq.${sharedTeamId}` },
          () => invalidate(queryKey),
        )
      }

      // Always also listen to user_id — catches the user's own changes
      // (personal-team templates) regardless of team state. Duplicate
      // fires with the team filter above are collapsed by `invalidate`.
      channel.on(
        'postgres_changes',
        { event: '*', schema: 'public', table: route.table, filter: `user_id=eq.${userId}` },
        () => invalidate(queryKey),
      )
    }

    // CHANNEL_ERROR fires on every transient WebSocket hiccup (idle-
    // reconnect, brief network drop, server restart). supabase-js
    // auto-retries and the channel recovers within a second or two,
    // the app is unaffected. Previously every occurrence logged at
    // `error` and paged Sentry — cross-tab sync confirmed working
    // throughout, so those pages were pure noise (see ADR 0039-era
    // post-mortem in PR #24).
    //
    // Treat early CHANNEL_ERRORs as informational (console only) and
    // only escalate once we've seen ERROR_ESCALATION_THRESHOLD in a
    // row without an intervening SUBSCRIBED — that's the signature of
    // a truly stuck channel (bad publication, RLS denial, schema
    // drift) that actually warrants a human look.
    const ERROR_ESCALATION_THRESHOLD = 3
    let consecutiveErrors = 0

    channel.subscribe((status, err) => {
      if (status === 'SUBSCRIBED') {
        if (consecutiveErrors > 0) {
          log.info('realtime channel recovered', {
            userId,
            sharedTeamId,
            afterErrors: consecutiveErrors,
          })
        } else {
          log.info('realtime channel subscribed', { userId, sharedTeamId })
        }
        consecutiveErrors = 0
      } else if (status === 'CHANNEL_ERROR') {
        consecutiveErrors += 1
        const payload = {
          userId,
          sharedTeamId,
          error: err?.message,
          consecutive: consecutiveErrors,
        }
        if (consecutiveErrors >= ERROR_ESCALATION_THRESHOLD) {
          log.error('realtime channel stuck (no recovery after retries)', payload)
        } else {
          log.warn('realtime channel error — supabase will auto-reconnect', payload)
        }
      } else if (status === 'TIMED_OUT') {
        log.warn('realtime channel timed out — supabase will auto-reconnect', { userId, sharedTeamId })
      } else if (status === 'CLOSED') {
        log.info('realtime channel closed', { userId, sharedTeamId })
      }
    })

    return () => {
      supabase.removeChannel(channel)
    }
  }, [userId, sharedTeamId, qc])

  return null
}
